// What the two surfaces draw, decided once.
//
// The words moved into `copy.ts` a long time ago and stopped the surfaces
// disagreeing about sentences; they went on disagreeing about structure — how
// many cells the contract strip has, whether the title is a link, which block
// stands where the reader is out of the queue. So the structure is derived
// here, and each surface only paints what these functions return.

import { compactDamage, exactDamage, percentWords, rateWords, WORDS } from "./copy";

/** How a cell's value is set: a figure, a name, or the battle multiplier, which
 * is the one thing in the strip that is always gold. */
export type CellTone = "plain" | "mono" | "muted" | "gold";

export interface ContractCell {
  label: string;
  value: string;
  tone: CellTone;
}

/**
 * The contract terms over the bar that measures them.
 *
 * On the queue screen the terms are cells with their own labels rather than one
 * run-on line: a reader checking the rate should not have to parse a sentence
 * to find it. The battle is not named again — the section title above carries
 * it — so the cell holds only what the name says after the battle's own word.
 */
export function contractCells(input: {
  /** The contract's own battle string, "<battle> · <side> · <round>" or
   * whatever the game gives. */
  battle: string | null;
  /** The battle the panel is pointed at, for a target set by hand. */
  battleName: string | null;
  poster: string | null;
  expected: number;
  ratePerK: number | null;
  bonusFactor: number | null;
  handSet: boolean;
}): ContractCell[] {
  if (input.handSet) {
    return [
      { label: "battle", value: input.battleName || WORDS.none, tone: "plain" },
      { label: "contract", value: "none · set by hand", tone: "muted" },
      { label: "expected", value: exactDamage(input.expected), tone: "mono" },
    ];
  }
  const parts = (input.battle || "").split(" · ");
  return [
    { label: "battle", value: parts.slice(1).join(" · ") || parts[0] || WORDS.none, tone: "plain" },
    { label: "posted by", value: input.poster || WORDS.none, tone: "plain" },
    { label: "expected", value: exactDamage(input.expected), tone: "mono" },
    { label: "rate", value: rateWords(input.ratePerK), tone: "mono" },
    {
      label: "battle bonus",
      value: input.bonusFactor === null ? WORDS.bonusUnknown : percentWords(input.bonusFactor),
      tone: "gold",
    },
  ];
}

export interface BattleLink {
  href: string;
  /** The extension is already inside the game, so it moves the page it is
   * standing on; the board is a separate tab and opens another. */
  target: "_self" | "_blank";
  title: string;
}

/**
 * The way from the queue into the fight.
 *
 * There is no link when the reader is already standing on that battle: a link
 * to the page you are on reads as a different page and is not one.
 */
export function battleLink(input: {
  battleId: string | null;
  /** The battle the reader's own page is showing, when the surface can know
   * it. The board never can, and passes null. */
  pageBattleId: string | null;
  inGame: boolean;
}): BattleLink | null {
  if (!input.battleId) return null;
  if (input.pageBattleId && input.pageBattleId === input.battleId) return null;
  return {
    href: "https://app.warera.io/battle/" + input.battleId,
    target: input.inGame ? "_self" : "_blank",
    title: input.inGame
      ? "open this battle on the game page"
      : "open this battle in the game · new tab",
  };
}

export interface QueueUpButton {
  label: string;
  /** The capacity it will queue them at, beside the verb rather than under it:
   * the reader decides on the figure, not on the word. */
  with: string;
  capacity: number;
}

/**
 * The one action a member out of the queue opens either surface to take.
 *
 * It carries the figure it commits, because pressing it commits that figure —
 * the presets and the typed field that used to stand here belonged to a form
 * the reader had to fill in before the unit could count on them. Once they are
 * in, nothing stands here: their own ledger row holds the figure and the way
 * out.
 */
export function queueUpButton(capacity: number): QueueUpButton {
  const rounded = Math.max(0, Math.round(capacity));
  return { label: WORDS.queueUp, with: compactDamage(rounded), capacity: rounded };
}
